import SurveyButton from '@/components/Base/SurveyButton';
import SurveyBackgroundLayout from '@/components/layout/SurveyBackgroundLayout';
import {
  SurveyContentPanelInset,
  SurveyFormPanel,
  surveyQuestionTextClass,
} from '@/components/layout/SurveyContentPanel';
import SurveyValidationAlert from '@/components/survey/SurveyValidationAlert';
import { useSurveyLanguage } from '@/hooks/useSurveyLanguage';
import { useTranslation } from '@/hooks/useTranslation';
import { isPrevBrandComplete } from '@/lib/prevBrandStorage';
import {
  PREV_BRAND_REASON_KEYS,
  readPrevBrandAnswerAnswers,
  writePrevBrandAnswerAnswers,
  type PrevBrandAnswerAnswers,
  type PrevBrandReasonKey,
} from '@/lib/prevBrandAnswerStorage';
import { clearOtherPrevBrandAnswerAnswers } from '@/lib/otherPrevBrandAnswerStorage';
import { clearOtherPrevBrandAnswers } from '@/lib/otherPrevBrandStorage';
import { clearOtherProductTypeAnswers } from '@/lib/otherProductTypeStorage';
import { surveyChoiceInputClass } from '@/lib/surveyChoiceInput';
import { surveyProfileRedirect, surveyQuestionnaireRedirect } from '@/lib/surveyFlow';
import { useAppSelector } from '@/stores/hooks';
import { selectOtpDraft, selectPdpaConsent, selectSurveyScreening } from '@/stores/selectors';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

const inputClass =
  'min-w-0 w-full rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

const labelClass = 'text-left text-[15px] font-medium leading-tight text-black sm:text-[16px]';

const hintClass = 'm-0 text-left text-[14px] leading-tight text-black/80 sm:text-[15px]';

const textareaClass =
  'min-w-0 w-full resize-none rounded-sm border border-slate-800 bg-white px-2 py-2 text-[15px] text-slate-900 outline-none focus:border-slate-600 sm:text-[16px]';

function ReasonOption({
  id,
  label,
  checked,
  onToggle,
}: {
  id: string;
  label: string;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-start gap-1.5">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        className={surveyChoiceInputClass}
      />
      <span className={labelClass}>{label}</span>
    </label>
  );
}

export default function PrevBrandAnswer() {
  const navigate = useNavigate();
  const { surveyLanguage } = useSurveyLanguage();
  const { t } = useTranslation();
  const surveyScreening = useAppSelector(selectSurveyScreening);
  const pdpaConsent = useAppSelector(selectPdpaConsent);
  const { customerUuid, otpVerified, questionnaireSet } = useAppSelector(selectOtpDraft);

  const [answers, setAnswers] = useState<PrevBrandAnswerAnswers>(() => readPrevBrandAnswerAnswers());
  const [warning, setWarning] = useState<string | null>(null);

  useEffect(() => {
    writePrevBrandAnswerAnswers(answers);
  }, [answers]);

  if (surveyLanguage == null) return <Navigate to="/" replace />;
  if (surveyScreening == null) return <Navigate to="/age_smoker" replace />;
  if (!pdpaConsent) return <Navigate to="/pdpa" replace />;
  if (!customerUuid || !otpVerified) return <Navigate to="/phone" replace />;
  const questionnaireRedirect = surveyQuestionnaireRedirect(questionnaireSet);
  if (questionnaireRedirect) return <Navigate to={questionnaireRedirect} replace />;
  const profileRedirect = surveyProfileRedirect(questionnaireSet);
  if (profileRedirect) return <Navigate to={profileRedirect} replace />;
  if (questionnaireSet === 'B') return <Navigate to="/ldproductnow" replace />;
  if (!isPrevBrandComplete()) return <Navigate to="/survey/prevbrand" replace />;

  const toggleReason = (key: PrevBrandReasonKey) => {
    setAnswers((prev) => ({
      ...prev,
      [key]: {
        checked: !prev[key].checked,
        elaboration: prev[key].elaboration,
      },
    }));
    setWarning(null);
  };

  const handleContinue = () => {
    const hasReason = PREV_BRAND_REASON_KEYS.some((key) => answers[key].checked);

    if (!hasReason) {
      setWarning(t.prevBrandAnswer.reasonRequiredWarning);
      return;
    }
    if (!answers.otherReason.trim()) {
      setWarning(t.prevBrandAnswer.elaborateAnswerWarning);
      return;
    }
    if (answers.smokedOtherProduct !== 'yes' && answers.smokedOtherProduct !== 'no') {
      setWarning(t.prevBrandAnswer.otherProductWarning);
      return;
    }

    writePrevBrandAnswerAnswers(answers);
    setWarning(null);
    navigate(answers.smokedOtherProduct === 'no' ? '/whyldnow' : '/survey/prevbrand-other');
  };

  return (
    <SurveyBackgroundLayout>
      <div className="relative z-10 flex h-[100dvh] max-h-[100dvh] flex-col pb-4">
        <div className="mx-auto flex min-h-0 w-full max-w-full flex-1 flex-col pt-[9dvh]">
          <SurveyContentPanelInset className="min-h-0 flex-1 overflow-y-auto">
            <div className="flex flex-col gap-3 pb-4">
              <SurveyFormPanel className="flex flex-col gap-2">
                <p className={surveyQuestionTextClass}>{t.prevBrandAnswer.whyQuestion}</p>

                {PREV_BRAND_REASON_KEYS.map((key) => (
                  <ReasonOption
                    key={key}
                    id={`reason-${key}`}
                    label={t.prevBrandAnswer[key]}
                    checked={answers[key].checked}
                    onToggle={() => toggleReason(key)}
                  />
                ))}

                <div className="flex flex-col gap-1">
                  <p className={hintClass}>{t.prevBrandAnswer.elaborateAnswer}</p>
                  <textarea
                    id="reason-elaboration"
                    rows={3}
                    required
                    value={answers.otherReason}
                    onChange={(e) => {
                      setAnswers((prev) => ({ ...prev, otherReason: e.target.value }));
                      setWarning(null);
                    }}
                    className={textareaClass}
                  />
                </div>
              </SurveyFormPanel>

              <SurveyFormPanel className="flex flex-col gap-1.5">
                <p id="smoked-other-product-question" className={surveyQuestionTextClass}>
                  {t.prevBrandAnswer.otherProductQuestion}
                </p>
                <select
                  id="smoked-other-product"
                  value={answers.smokedOtherProduct}
                  onChange={(e) => {
                    const smokedOtherProduct = e.target.value as PrevBrandAnswerAnswers['smokedOtherProduct'];
                    if (smokedOtherProduct === 'no') {
                      clearOtherPrevBrandAnswers();
                      clearOtherPrevBrandAnswerAnswers();
                      clearOtherProductTypeAnswers();
                    }
                    setAnswers((prev) => ({
                      ...prev,
                      smokedOtherProduct,
                    }));
                    setWarning(null);
                  }}
                  className={inputClass}
                  aria-labelledby="smoked-other-product-question"
                >
                  <option value="" disabled>
                    {t.prevBrandAnswer.selectYesNo}
                  </option>
                  <option value="yes">{t.prevBrandAnswer.yes}</option>
                  <option value="no">{t.prevBrandAnswer.no}</option>
                </select>
              </SurveyFormPanel>
            </div>
          </SurveyContentPanelInset>

          <SurveyContentPanelInset className="mt-2 shrink-0">
            <div className="flex flex-col items-center gap-3">
              {warning != null ? <SurveyValidationAlert>{warning}</SurveyValidationAlert> : null}
              <SurveyButton className="w-full" onClick={handleContinue}>
                {t.prevBrandAnswer.continue}
              </SurveyButton>
            </div>
          </SurveyContentPanelInset>
        </div>
      </div>
    </SurveyBackgroundLayout>
  );
}
